import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Admin-only i18n (Prompt 18). Independent from the public LanguageContext
 * so the admin can be in Spanish while the public site keeps its EN/ES/RU
 * routing untouched.
 *
 * Default language: ES. Preference persisted in localStorage under
 * `slr.admin.lang`. Consumers use `useAdminT()` -> t("some.key").
 * Missing keys fall back to English, then to the raw key.
 */

const STORAGE_KEY = "slr.admin.lang";
const DEFAULT_LANG = "es";
const SUPPORTED = ["es", "en"];

const AdminLangCtx = createContext({ lang: DEFAULT_LANG, setLang: () => {}, t: (k) => k });

export function AdminLanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && SUPPORTED.includes(stored)) return stored;
    } catch (_) { /* ignore */ }
    return DEFAULT_LANG;
  });

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* ignore */ }
    setLangState(next);
    // update <html lang="…"> only for the admin sub-tree scope
    if (typeof document !== "undefined") {
      // Only touch the html lang when we are inside the admin panel to avoid
      // interfering with the public SPA that manages its own lang tag.
      if (window.location.pathname.startsWith("/admin")) {
        document.documentElement.setAttribute("lang", next);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined" && window.location.pathname.startsWith("/admin")) {
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  const t = useCallback((key, params) => {
    const dict = STRINGS[lang] || {};
    const en = STRINGS.en || {};
    let out = dict[key] != null ? dict[key] : (en[key] != null ? en[key] : key);
    if (params && typeof out === "string") {
      for (const [k, v] of Object.entries(params)) {
        out = out.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
      }
    }
    return out;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <AdminLangCtx.Provider value={value}>{children}</AdminLangCtx.Provider>;
}

export function useAdminT() {
  return useContext(AdminLangCtx);
}

// ---------------------------------------------------------------------------
// String table
// ---------------------------------------------------------------------------
export const STRINGS = {
  en: {
    // sidebar
    "nav.dashboard": "Dashboard",
    "nav.vehicles": "Vehicles",
    "nav.routes": "Routes",
    "nav.bookings": "Bookings",
    "nav.tracking": "Tracking",
    "nav.collaborators": "Collaborators",
    "nav.guides": "Guides",
    "nav.ai_seo": "AI SEO Studio",
    "nav.settings": "Settings",
    "nav.legal": "Legal",
    "nav.password": "Password",
    "nav.logout": "Logout",
    "common.language": "Language",
    "common.loading": "Loading…",
    "common.refresh": "Refresh",
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.delete": "Delete",
    "common.confirm": "Confirm",
    "common.close": "Close",
    "common.yes": "Yes",
    "common.no": "No",
    "common.search": "Search",
    "common.filter": "Filter",
    "common.status": "Status",
    "common.actions": "Actions",
    "common.error": "Error",
    "common.empty": "No items yet.",
    // AI SEO Studio
    "ai.title": "AI SEO Studio",
    "ai.subtitle": "Generate SEO drafts grounded on the active fleet. Nothing publishes automatically — every draft must be approved before it appears in Guides.",
    "ai.model_label": "Claude Sonnet 4.6 · ES / EN / RU / FR / DE / IT / NL / PL",
    "ai.tab.studio": "Studio",
    "ai.tab.gsc": "SEO Performance",
    "ai.mode.analyze": "Improve existing",
    "ai.mode.create": "Create new",
    "ai.mode.discover": "Discover opportunities",
    "ai.form.analyze_heading": "Improve an existing guide",
    "ai.form.create_heading": "Create a new SEO page",
    "ai.form.discover_heading": "Discover SEO opportunities",
    "ai.form.guide": "Guide",
    "ai.form.guide_placeholder": "— pick a guide —",
    "ai.form.topic": "Topic *",
    "ai.form.topic_placeholder": "e.g. Alquiler de coches deportivos en Alicante",
    "ai.form.category": "Category",
    "ai.form.category.guide": "Guide",
    "ai.form.category.route": "Route",
    "ai.form.category.event": "Event",
    "ai.form.seeds": "Seed topics (optional, comma-separated)",
    "ai.form.seeds_placeholder": "e.g. alquiler V8 Alicante, coches lujo Benidorm",
    "ai.form.web_search": "Use live web search for trend signals (slower, richer)",
    "ai.form.focus_keyword": "Focus keyword",
    "ai.form.focus_placeholder": "e.g. alquiler coche lujo Alicante",
    "ai.form.notes": "Editor notes (optional)",
    "ai.form.notes_placeholder": "Any angle, tone, or constraint you want the AI to respect.",
    "ai.form.submit": "Generate draft",
    "ai.form.submit_queuing": "Queuing…",
    "ai.form.submit_waiting": "Waiting on current job…",
    "ai.form.hint": "Generations take ~30-90 seconds. The draft appears below and auto-refreshes.",
    "ai.drafts.heading": "Recent drafts",
    "ai.drafts.empty": "No drafts yet. Pick a mode above and generate your first one.",
    "ai.status.pending": "Generating…",
    "ai.status.draft": "Draft ready",
    "ai.status.approved": "Approved",
    "ai.status.failed": "Failed",
    "ai.field.title": "Title",
    "ai.field.seo_title": "SEO Title",
    "ai.field.meta": "Meta description",
    "ai.field.intro": "Intro",
    "ai.field.body": "Body",
    "ai.field.improvements": "Improvements",
    "ai.field.rec_fleet": "Recommended fleet",
    "ai.qa.flagged_one": "Factual QA flagged 1 issue:",
    "ai.qa.flagged_many": "Factual QA flagged {n} issues:",
    "ai.originality.label": "Originality {score}% · overlap {overlap}%",
    "ai.actions.approve_unpublished": "Approve as unpublished guide",
    "ai.actions.approve_publish": "Approve & publish now",
    "ai.actions.audit": "Re-audit against fleet",
    "ai.actions.confirm_qa_override": "Factual QA is blocking approval. Are you sure you want to override and approve anyway?",
    "ai.actions.force_override": "Force override (danger)",
    "ai.errors.qa_blocked": "Approval blocked — Factual QA raised issues.",
    "ai.errors.qa_use_override": "Fix the flagged claims, re-audit, or use the red 'Force override' button if you accept the risk.",
    "ai.discover.no_data": "No opportunities generated.",
    // GSC panel
    "gsc.title": "SEO Performance",
    "gsc.subtitle": "Real Google Search Console data. Grounded, never invented.",
    "gsc.connected": "Connected",
    "gsc.not_connected": "Not connected",
    "gsc.connect_btn": "Connect Google Search Console",
    "gsc.reconnect": "Reconnect",
    "gsc.disconnect": "Disconnect",
    "gsc.pick_property": "Pick property",
    "gsc.change_property": "Change",
    "gsc.select_property_heading": "Select a property",
    "gsc.select_property_hint": "Connected to Google. Now pick which property you want to analyse.",
    "gsc.empty_headline": "Connect Google Search Console",
    "gsc.empty_body": "Unlock real SEO performance data: clicks, impressions, CTR, position, and opportunity detection based on your actual Search Console traffic.",
    "gsc.setup_required": "Setup required",
    "gsc.last_sync": "Last sync {when}",
    "gsc.kpi.clicks": "Clicks",
    "gsc.kpi.impressions": "Impressions",
    "gsc.kpi.ctr": "CTR",
    "gsc.kpi.position": "Avg. Position",
    "gsc.kpi.delta": "vs prev",
    "gsc.not_enough_data": "Not enough Search Console data yet in this window. Try a longer range or wait a few days.",
    "gsc.range.7d": "Last 7 days",
    "gsc.range.28d": "Last 28 days",
    "gsc.range.3m": "Last 3 months",
    "gsc.range.6m": "Last 6 months",
    "gsc.range.12m": "Last 12 months",
    "gsc.advisor.title": "AI SEO Advisor",
    "gsc.advisor.run": "Analyze my SEO",
    "gsc.advisor.running": "Analysing…",
    "gsc.advisor.explainer": "Runs Claude over your GSC data + fleet to surface the top priorities to work on this week. Never a ranking guarantee.",
    "gsc.advisor.analyzed_at": "Analysed {when}",
    "gsc.advisor.reason": "Reason",
    "gsc.advisor.evidence": "Evidence",
    "gsc.advisor.action": "Action",
    "gsc.advisor.impact": "Impact",
    "gsc.advisor.effort": "Effort",
    "gsc.opp.quick_wins": "Quick Wins",
    "gsc.opp.striking": "Striking Distance",
    "gsc.opp.low_ctr": "High Impressions, Low CTR",
    "gsc.opp.content_gap": "Content Gap",
    "gsc.opp.cannibalization": "Potential Cannibalization",
    "gsc.opp.declining": "Declining Content",
    "gsc.opp.improve_btn": "Improve",
    "gsc.opp.create_btn": "Create draft",
    "gsc.opp.analyze_btn": "Analyze",
    "gsc.opp.no_quick_wins": "No quick wins detected in this window.",
    "gsc.opp.no_cannibalization": "No cannibalization detected.",
    "gsc.opp.no_declining": "No declining pages detected.",
    "gsc.table.top_queries": "Top Queries",
    "gsc.table.top_pages": "Top Pages",
    "gsc.table.query": "Query",
    "gsc.table.page": "Page",
    "gsc.table.clicks": "Clicks",
    "gsc.table.impressions": "Impr.",
    "gsc.table.ctr": "CTR",
    "gsc.table.position": "Position",
    "gsc.table.trend": "Trend",
    "gsc.table.filter_queries": "Filter queries…",
    "gsc.intent.commercial": "commercial",
    "gsc.intent.local": "local",
    // Dashboard
    "dashboard.title": "Dashboard",
    // Vehicles
    "vehicles.title": "Vehicles",
    // Routes
    "routes.title": "Routes",
    // Bookings
    "bookings.title": "Bookings",
    // Tracking
    "tracking.title": "Tracking",
    // Collaborators
    "collaborators.title": "Collaborators",
    // Guides
    "guides.title": "Guides",
    // Settings
    "settings.title": "Settings",
    // Legal
    "legal.title": "Legal pages",
    // Password
    "password.title": "Change password",
  },
  es: {
    // sidebar
    "nav.dashboard": "Panel",
    "nav.vehicles": "Vehículos",
    "nav.routes": "Rutas",
    "nav.bookings": "Reservas",
    "nav.tracking": "Seguimiento",
    "nav.collaborators": "Colaboradores",
    "nav.guides": "Guías",
    "nav.ai_seo": "AI SEO Studio",
    "nav.settings": "Configuración",
    "nav.legal": "Legal",
    "nav.password": "Contraseña",
    "nav.logout": "Cerrar sesión",
    "common.language": "Idioma",
    "common.loading": "Cargando…",
    "common.refresh": "Actualizar",
    "common.cancel": "Cancelar",
    "common.save": "Guardar",
    "common.delete": "Eliminar",
    "common.confirm": "Confirmar",
    "common.close": "Cerrar",
    "common.yes": "Sí",
    "common.no": "No",
    "common.search": "Buscar",
    "common.filter": "Filtrar",
    "common.status": "Estado",
    "common.actions": "Acciones",
    "common.error": "Error",
    "common.empty": "Aún no hay elementos.",
    // AI SEO Studio
    "ai.title": "AI SEO Studio",
    "ai.subtitle": "Genera borradores SEO basados en la flota activa. Nada se publica automáticamente — cada borrador debe aprobarse antes de aparecer en Guías.",
    "ai.model_label": "Claude Sonnet 4.6 · ES / EN / RU / FR / DE / IT / NL / PL",
    "ai.tab.studio": "Estudio",
    "ai.tab.gsc": "Rendimiento SEO",
    "ai.mode.analyze": "Mejorar existente",
    "ai.mode.create": "Crear nueva",
    "ai.mode.discover": "Descubrir oportunidades",
    "ai.form.analyze_heading": "Mejorar una guía existente",
    "ai.form.create_heading": "Crear una nueva página SEO",
    "ai.form.discover_heading": "Descubrir oportunidades SEO",
    "ai.form.guide": "Guía",
    "ai.form.guide_placeholder": "— elige una guía —",
    "ai.form.topic": "Tema *",
    "ai.form.topic_placeholder": "p. ej. Alquiler de coches deportivos en Alicante",
    "ai.form.category": "Categoría",
    "ai.form.category.guide": "Guía",
    "ai.form.category.route": "Ruta",
    "ai.form.category.event": "Evento",
    "ai.form.seeds": "Temas semilla (opcional, separados por comas)",
    "ai.form.seeds_placeholder": "p. ej. alquiler V8 Alicante, coches lujo Benidorm",
    "ai.form.web_search": "Usar búsqueda web en vivo para señales de tendencia (más lento, más rico)",
    "ai.form.focus_keyword": "Palabra clave principal",
    "ai.form.focus_placeholder": "p. ej. alquiler coche lujo Alicante",
    "ai.form.notes": "Notas del editor (opcional)",
    "ai.form.notes_placeholder": "Cualquier ángulo, tono o restricción que quieras que la IA respete.",
    "ai.form.submit": "Generar borrador",
    "ai.form.submit_queuing": "Encolando…",
    "ai.form.submit_waiting": "Esperando al trabajo actual…",
    "ai.form.hint": "Las generaciones tardan ~30-90 segundos. El borrador aparece abajo y se refresca solo.",
    "ai.drafts.heading": "Borradores recientes",
    "ai.drafts.empty": "Aún no hay borradores. Elige un modo arriba y genera el primero.",
    "ai.status.pending": "Generando…",
    "ai.status.draft": "Borrador listo",
    "ai.status.approved": "Aprobado",
    "ai.status.failed": "Falló",
    "ai.field.title": "Título",
    "ai.field.seo_title": "Título SEO",
    "ai.field.meta": "Meta descripción",
    "ai.field.intro": "Introducción",
    "ai.field.body": "Contenido",
    "ai.field.improvements": "Mejoras",
    "ai.field.rec_fleet": "Flota recomendada",
    "ai.qa.flagged_one": "Factual QA detectó 1 incidencia:",
    "ai.qa.flagged_many": "Factual QA detectó {n} incidencias:",
    "ai.originality.label": "Originalidad {score}% · solape {overlap}%",
    "ai.actions.approve_unpublished": "Aprobar como guía no publicada",
    "ai.actions.approve_publish": "Aprobar y publicar ahora",
    "ai.actions.audit": "Reauditar contra flota",
    "ai.actions.confirm_qa_override": "Factual QA está bloqueando la aprobación. ¿Seguro que quieres omitirlo y aprobar de todos modos?",
    "ai.actions.force_override": "Forzar aprobación (peligro)",
    "ai.errors.qa_blocked": "Aprobación bloqueada — Factual QA detectó incidencias.",
    "ai.errors.qa_use_override": "Corrige los datos marcados, re-audita, o usa el botón rojo 'Forzar aprobación' si aceptas el riesgo.",
    "ai.discover.no_data": "No se generaron oportunidades.",
    // GSC panel
    "gsc.title": "Rendimiento SEO",
    "gsc.subtitle": "Datos reales de Google Search Console. Fundamentados, nunca inventados.",
    "gsc.connected": "Conectado",
    "gsc.not_connected": "No conectado",
    "gsc.connect_btn": "Conectar Google Search Console",
    "gsc.reconnect": "Reconectar",
    "gsc.disconnect": "Desconectar",
    "gsc.pick_property": "Seleccionar propiedad",
    "gsc.change_property": "Cambiar",
    "gsc.select_property_heading": "Selecciona una propiedad",
    "gsc.select_property_hint": "Conectado a Google. Ahora elige qué propiedad quieres analizar.",
    "gsc.empty_headline": "Conecta Google Search Console",
    "gsc.empty_body": "Desbloquea datos reales de rendimiento SEO: clics, impresiones, CTR, posición y detección de oportunidades basadas en tu tráfico real de Search Console.",
    "gsc.setup_required": "Se requiere configuración",
    "gsc.last_sync": "Última sincronización {when}",
    "gsc.kpi.clicks": "Clics",
    "gsc.kpi.impressions": "Impresiones",
    "gsc.kpi.ctr": "CTR",
    "gsc.kpi.position": "Posición media",
    "gsc.kpi.delta": "vs anterior",
    "gsc.not_enough_data": "Aún no hay suficientes datos de Search Console en este periodo. Prueba con un rango más largo o espera unos días.",
    "gsc.range.7d": "Últimos 7 días",
    "gsc.range.28d": "Últimos 28 días",
    "gsc.range.3m": "Últimos 3 meses",
    "gsc.range.6m": "Últimos 6 meses",
    "gsc.range.12m": "Últimos 12 meses",
    "gsc.advisor.title": "Asesor SEO con IA",
    "gsc.advisor.run": "Analizar mi SEO",
    "gsc.advisor.running": "Analizando…",
    "gsc.advisor.explainer": "Ejecuta Claude sobre tus datos de GSC + flota para destacar las prioridades de esta semana. Nunca garantiza posiciones.",
    "gsc.advisor.analyzed_at": "Analizado {when}",
    "gsc.advisor.reason": "Motivo",
    "gsc.advisor.evidence": "Evidencia",
    "gsc.advisor.action": "Acción",
    "gsc.advisor.impact": "Impacto",
    "gsc.advisor.effort": "Esfuerzo",
    "gsc.opp.quick_wins": "Oportunidades rápidas",
    "gsc.opp.striking": "A un paso del top",
    "gsc.opp.low_ctr": "Muchas impresiones, CTR bajo",
    "gsc.opp.content_gap": "Hueco de contenido",
    "gsc.opp.cannibalization": "Posible canibalización",
    "gsc.opp.declining": "Contenido en declive",
    "gsc.opp.improve_btn": "Mejorar",
    "gsc.opp.create_btn": "Crear borrador",
    "gsc.opp.analyze_btn": "Analizar",
    "gsc.opp.no_quick_wins": "No se detectan oportunidades rápidas en este periodo.",
    "gsc.opp.no_cannibalization": "Sin canibalización detectada.",
    "gsc.opp.no_declining": "Sin páginas en declive detectadas.",
    "gsc.table.top_queries": "Principales búsquedas",
    "gsc.table.top_pages": "Principales páginas",
    "gsc.table.query": "Búsqueda",
    "gsc.table.page": "Página",
    "gsc.table.clicks": "Clics",
    "gsc.table.impressions": "Impr.",
    "gsc.table.ctr": "CTR",
    "gsc.table.position": "Posición",
    "gsc.table.trend": "Tendencia",
    "gsc.table.filter_queries": "Filtrar búsquedas…",
    "gsc.intent.commercial": "comercial",
    "gsc.intent.local": "local",
    // Dashboard
    "dashboard.title": "Panel",
    // Vehicles
    "vehicles.title": "Vehículos",
    // Routes
    "routes.title": "Rutas",
    // Bookings
    "bookings.title": "Reservas",
    // Tracking
    "tracking.title": "Seguimiento",
    // Collaborators
    "collaborators.title": "Colaboradores",
    // Guides
    "guides.title": "Guías",
    // Settings
    "settings.title": "Configuración",
    // Legal
    "legal.title": "Páginas legales",
    // Password
    "password.title": "Cambiar contraseña",
  },
};
